import pick from 'lodash/pick';
import config from '../../config';
import { resetToStartOfDay, getDefaultTimeZoneOnBrowser } from '../../util/dates';
import { types as sdkTypes } from '../../util/sdkLoader';
import { listingDetailIncludeParams } from '../../util/imageVariants';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { availabilityExceptionsAPI, subscriptionsAPI, usersAPI, listingsAPI } from '../../api';
import { googleStorageAPI } from '../../util/api';
import {
  denormalisedResponseEntities,
  isUserInvitedToOrganization,
  isUserInvitedToTeam,
} from '../../util/data';
import { TRANSITION_ENQUIRE } from '../../util/transaction';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  fetchCurrentUser,
  fetchCurrentUserHasOrdersSuccess,
  queryRecentlyViewedListings,
  saveRecentlyViewedListingId,
} from '../../ducks/user.duck';
import { payoutsEnabled } from '../../ducks/stripe.duck.js';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { fetchComments } from '../CommentsPage/CommentsPage.duck';
import { episodeRsvpAPI } from '../../api/clients/episodeRsvpAPI';
import { requestBooking, sendMessage } from '../BookingPage/BookingPage.duck';
import { formatDateString } from '../../util/dates';

const { UUID } = sdkTypes;

const QUERY_EXPERIENCES_RESULT_PAGE_SIZE = 12;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/LiveRoomPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/LiveRoomPage/SHOW_LISTING_REQUEST';

export const QUERY_EXPERIENCES_REQUEST = 'app/LiveRoomPage/QUERY_EXPERIENCES_REQUEST';
export const QUERY_EXPERIENCES_SUCCESS = 'app/LiveRoomPage/QUERY_EXPERIENCES_SUCCESS';
export const QUERY_EXPERIENCES_ERROR = 'app/LiveRoomPage/QUERY_EXPERIENCES_ERROR';

export const FETCH_AVAILABILITY_EXCEPTIONS_REQUEST =
  'app/LiveRoomPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS =
  'app/LiveRoomPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_AVAILABILITY_EXCEPTIONS_ERROR =
  'app/LiveRoomPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/LiveRoomPage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/LiveRoomPage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/LiveRoomPage/SEND_ENQUIRY_ERROR';

export const QUERY_USERS_REQUEST = 'app/LiveRoomPage/QUERY_USERS_REQUEST';
export const QUERY_USERS_SUCCESS = 'app/LiveRoomPage/QUERY_USERS_SUCCESS';
export const QUERY_USERS_ERROR = 'app/LiveRoomPage/QUERY_USERS_ERROR';

export const CREATE_FREE_SUBSCRIPTION_REQUEST = 'app/LiveRoomPage/CREATE_FREE_SUBSCRIPTION_REQUEST';
export const CREATE_FREE_SUBSCRIPTION_SUCCESS = 'app/LiveRoomPage/CREATE_FREE_SUBSCRIPTION_SUCCESS';
export const CREATE_FREE_SUBSCRIPTION_ERROR = 'app/LiveRoomPage/CREATE_FREE_SUBSCRIPTION_ERROR';

export const FILTER_AVAILABILITY_EXCEPTIONS_SUCCESS =
  'app/LiveRoomPage/FILTER_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_AUTHOR_PAYOUTS_ENABLED = 'app/LiveRoomPage/FETCH_AUTHOR_PAYOUTS_ENABLED';

export const UPLOAD_VIDEO_REQUEST = 'app/LiveRoomPage/UPLOAD_VIDEO_REQUEST';
export const UPLOAD_VIDEO_SUCCESS = 'app/LiveRoomPage/UPLOAD_VIDEO_SUCCESS';
export const UPLOAD_VIDEO_ERROR = 'app/LiveRoomPage/UPLOAD_VIDEO_ERROR';

export const REMOVE_VIDEO = 'app/LiveRoomPage/REMOVE_VIDEO';

export const UPDATE_LISTING_BEHIND_THE_SCENES_REQUEST =
  'app/LiveRoomPage/UPDATE_LISTING_BEHIND_THE_SCENES_REQUEST';
export const UPDATE_LISTING_BEHIND_THE_SCENES_SUCCESS =
  'app/LiveRoomPage/UPDATE_LISTING_BEHIND_THE_SCENES_SUCCESS';
export const UPDATE_LISTING_BEHIND_THE_SCENES_ERROR =
  'app/LiveRoomPage/UPDATE_LISTING_BEHIND_THE_SCENES_ERROR';

export const SET_BOOKED_TIMESLOT_IDS = 'app/LiveRoomPage/SET_BOOKED_TIMESLOT_IDS';

export const RESERVE_EPISODE_REQUEST = 'app/LiveRoomPage/RESERVE_EPISODE_REQUEST';
export const RESERVE_EPISODE_SUCCESS = 'app/LiveRoomPage/RESERVE_EPISODE_SUCCESS';
export const RESERVE_EPISODE_ERROR = 'app/LiveRoomPage/RESERVE_EPISODE_ERROR';

// ================ Reducer ================ //

const initialState = {
  // listing(s)
  id: null,
  sendEnquiryInProgress: false,
  sendEnquiryError: null,
  enquiryModalOpenForListingId: null,
  liveRoomIds: [],
  queryLiveRoomsInProgress: false,
  queryLiveRoomsError: null,

  // users
  userIds: [],
  queryUsersInProgress: false,
  queryUsersError: null,
  authorPayoutsEnabled: false,
  // exceptions
  fetchExceptionsError: null,
  fetchExceptionsInProgress: false,
  availabilityExceptions: [],
  filteredAvailabilityExceptions: [],
  // behind the scenes
  uploadedBehindTheScenesVideos: {},
  uploadVideoError: null,
  updateBehindTheScenesInProgress: false,
  updateBehindTheScenesError: null,

  createFreeSubscriptionInProgress: false,
  createFreeSubscriptionError: null,

  // User's booked timeslot IDs for this listing
  bookedTimeslotIds: [],
  // The episode being reserved from the schedule right now, and the last failure
  reserveEpisodeInProgressId: null,
  reserveEpisodeErrorId: null,
};

const resultIds = data => data.data.map(d => d.id);

const liveRoomPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      console.log('SET_INITIAL_VALUES called, resetting state');
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id };

    case QUERY_EXPERIENCES_REQUEST:
      return {
        ...state,
        queryLiveRoomsInProgress: true,
        queryLiveRoomsError: null,
      };
    case QUERY_EXPERIENCES_SUCCESS:
      return {
        ...state,
        liveRoomIds: resultIds(payload.data),
        queryLiveRoomsInProgress: false,
        queryLiveRoomsError: null,
      };
    case QUERY_EXPERIENCES_ERROR:
      return {
        ...state,
        liveRoomIds: [],
        queryLiveRoomsInProgress: false,
        queryLiveRoomsError: payload,
      };

    case SEND_ENQUIRY_REQUEST:
      return { ...state, sendEnquiryInProgress: true, sendEnquiryError: null };
    case SEND_ENQUIRY_SUCCESS:
      return { ...state, sendEnquiryInProgress: false };
    case SEND_ENQUIRY_ERROR:
      return { ...state, sendEnquiryInProgress: false, sendEnquiryError: payload };

    case QUERY_USERS_REQUEST:
      return {
        ...state,
        queryUsersInProgress: true,
        queryUsersError: null,
      };
    case QUERY_USERS_SUCCESS:
      return {
        ...state,
        userIds: resultIds(payload).filter(i => payload.queryIds.includes(i.uuid)),
        queryUsersInProgress: false,
        queryUsersError: null,
      };
    case QUERY_USERS_ERROR:
      return {
        ...state,
        userIds: [],
        queryUsersInProgress: false,
        queryUsersError: payload,
      };

    case FETCH_AVAILABILITY_EXCEPTIONS_REQUEST:
      return {
        ...state,
        availabilityExceptions: [],
        filteredAvailabilityExceptions: [],
        fetchExceptionsError: null,
        fetchExceptionsInProgress: true,
      };
    case FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS:
      return {
        ...state,
        availabilityExceptions: payload,
        filteredAvailabilityExceptions: payload,
        fetchExceptionsError: null,
        fetchExceptionsInProgress: false,
      };
    case FETCH_AVAILABILITY_EXCEPTIONS_ERROR:
      return {
        ...state,
        fetchExceptionsError: payload,
        fetchExceptionsInProgress: false,
      };
    case FILTER_AVAILABILITY_EXCEPTIONS_SUCCESS:
      return {
        ...state,
        filteredAvailabilityExceptions: payload,
      };

    case CREATE_FREE_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        createFreeSubscriptionInProgress: true,
        createFreeSubscriptionError: null,
      };
    case CREATE_FREE_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        createFreeSubscriptionInProgress: false,
        createFreeSubscriptionError: null,
      };
    case CREATE_FREE_SUBSCRIPTION_ERROR:
      return {
        ...state,
        createFreeSubscriptionInProgress: false,
        createFreeSubscriptionError: payload,
      };

    case FETCH_AUTHOR_PAYOUTS_ENABLED:
      return {
        ...state,
        authorPayoutsEnabled: payload,
      };

    case UPLOAD_VIDEO_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedVideos = {
        ...state.uploadedBehindTheScenesVideos,
        [payload.params.id]: {
          uploading: true,
          file: payload.params.file,
        },
      };
      return {
        ...state,
        uploadedBehindTheScenesVideos: uploadedVideos,
        uploadVideoError: null,
      };
    }
    case UPLOAD_VIDEO_SUCCESS: {
      // payload: { id: 'tempId', videoId: 'real-id', src, createdAt, file }
      console.log('UPLOAD_VIDEO_SUCCESS reducer:', payload);
      const { id: tempId, videoId, ...rest } = payload;
      const uploadedVideos = { ...state.uploadedBehindTheScenesVideos };

      console.log('Before removal - uploadedVideos keys:', Object.keys(uploadedVideos));
      console.log('Trying to remove tempId:', tempId);

      // Remove the temp entry with uploading: true
      delete uploadedVideos[tempId];

      console.log('After removal - uploadedVideos keys:', Object.keys(uploadedVideos));

      // Add the final entry with real video ID
      uploadedVideos[videoId] = { videoId, ...rest };

      console.log('Updated uploadedBehindTheScenesVideos:', uploadedVideos);
      return { ...state, uploadedBehindTheScenesVideos: uploadedVideos };
    }
    case UPLOAD_VIDEO_ERROR: {
      const { id, error } = payload;
      const uploadedVideos = { ...state.uploadedBehindTheScenesVideos };
      delete uploadedVideos[id];
      return { ...state, uploadedBehindTheScenesVideos: uploadedVideos, uploadVideoError: error };
    }
    case REMOVE_VIDEO: {
      const { videoId } = payload;
      const uploadedVideos = { ...state.uploadedBehindTheScenesVideos };
      delete uploadedVideos[videoId];
      return { ...state, uploadedBehindTheScenesVideos: uploadedVideos };
    }

    case UPDATE_LISTING_BEHIND_THE_SCENES_REQUEST:
      return {
        ...state,
        updateBehindTheScenesInProgress: true,
        updateBehindTheScenesError: null,
      };
    case UPDATE_LISTING_BEHIND_THE_SCENES_SUCCESS:
      return {
        ...state,
        updateBehindTheScenesInProgress: false,
        uploadedBehindTheScenesVideos: {}, // Clear temporary videos after successful save
      };
    case UPDATE_LISTING_BEHIND_THE_SCENES_ERROR:
      return {
        ...state,
        updateBehindTheScenesInProgress: false,
        updateBehindTheScenesError: payload,
      };

    case SET_BOOKED_TIMESLOT_IDS:
      return {
        ...state,
        bookedTimeslotIds: payload,
      };

    case RESERVE_EPISODE_REQUEST:
      return { ...state, reserveEpisodeInProgressId: payload, reserveEpisodeErrorId: null };
    case RESERVE_EPISODE_SUCCESS:
      return {
        ...state,
        reserveEpisodeInProgressId: null,
        bookedTimeslotIds: [...new Set([...state.bookedTimeslotIds, payload])],
      };
    case RESERVE_EPISODE_ERROR:
      return { ...state, reserveEpisodeInProgressId: null, reserveEpisodeErrorId: payload };

    default:
      return state;
  }
};

export default liveRoomPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const setBookedTimeslotIds = ids => ({
  type: SET_BOOKED_TIMESLOT_IDS,
  payload: ids,
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const queryExperiencesRequest = () => ({
  type: QUERY_EXPERIENCES_REQUEST,
});
export const queryExperiencesSuccess = response => ({
  type: QUERY_EXPERIENCES_SUCCESS,
  payload: { data: response.data },
});
export const queryLiveRoomsError = e => ({
  type: QUERY_EXPERIENCES_ERROR,
  error: true,
  payload: e,
});

export const sendEnquiryRequest = () => ({ type: SEND_ENQUIRY_REQUEST });
export const sendEnquirySuccess = () => ({ type: SEND_ENQUIRY_SUCCESS });
export const sendEnquiryError = e => ({ type: SEND_ENQUIRY_ERROR, error: true, payload: e });

export const queryUsersRequest = () => ({
  type: QUERY_USERS_REQUEST,
});
export const queryUsersSuccess = (response, queryIds) => ({
  type: QUERY_USERS_SUCCESS,
  payload: { data: response.data.data, queryIds },
});
export const queryUsersError = e => ({
  type: QUERY_USERS_ERROR,
  error: true,
  payload: e,
});

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = params => ({
  type: FETCH_AVAILABILITY_EXCEPTIONS_REQUEST,
  payload: { params },
});
export const fetchAvailabilityExceptionsSuccess = availabilityExceptions => ({
  type: FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS,
  payload: availabilityExceptions,
});
export const fetchAvailabilityExceptionsError = e => ({
  type: FETCH_AVAILABILITY_EXCEPTIONS_ERROR,
  error: true,
  payload: e,
});

export const createFreeSubscriptionRequest = () => ({
  type: CREATE_FREE_SUBSCRIPTION_REQUEST,
});
export const createFreeSubscriptionSuccess = () => ({
  type: CREATE_FREE_SUBSCRIPTION_SUCCESS,
});
export const createFreeSubscriptionError = e => ({
  type: CREATE_FREE_SUBSCRIPTION_ERROR,
  error: true,
  payload: e,
});

export const filterAvailabilityExceptionsSuccess = availabilityExceptions => ({
  type: FILTER_AVAILABILITY_EXCEPTIONS_SUCCESS,
  payload: availabilityExceptions,
});

export const fetchAuthorPayoutsEnabledSuccess = payoutsEnabled => ({
  type: FETCH_AUTHOR_PAYOUTS_ENABLED,
  payload: payoutsEnabled,
});

// Video upload action creators
export const uploadVideoRequest = params => ({
  type: UPLOAD_VIDEO_REQUEST,
  payload: { params },
});
export const uploadVideoSuccess = data => ({
  type: UPLOAD_VIDEO_SUCCESS,
  payload: data,
});
export const uploadVideoError = error => ({
  type: UPLOAD_VIDEO_ERROR,
  payload: error,
  error: true,
});
export const removeVideo = videoId => ({
  type: REMOVE_VIDEO,
  payload: { videoId },
});

// Update listing behind the scenes action creators
export const updateListingBehindTheScenesRequest = () => ({
  type: UPDATE_LISTING_BEHIND_THE_SCENES_REQUEST,
});
export const updateListingBehindTheScenesSuccess = response => ({
  type: UPDATE_LISTING_BEHIND_THE_SCENES_SUCCESS,
  payload: response,
});
export const updateListingBehindTheScenesError = e => ({
  type: UPDATE_LISTING_BEHIND_THE_SCENES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

// Read author's cached payouts-enabled flag straight off the listing's
// `author` include if present; only hit the endpoint on cache miss.
// The endpoint writes back into publicData.payoutsEnabled, so the next
// pageview avoids the Stripe roundtrip entirely.
export const fetchAuthorPayoutsEnabled = (authorId, cachedValue) => dispatch => {
  if (typeof cachedValue === 'boolean') {
    dispatch(fetchAuthorPayoutsEnabledSuccess(cachedValue));
    return;
  }
  dispatch(payoutsEnabled(authorId)).then(value => {
    dispatch(fetchAuthorPayoutsEnabledSuccess(value));
  });
};

const fetchListingTeamIds = (listing, dispatch) => {
  const teamFromPublicData = listing.attributes.publicData.team || [];
  const teamIds = teamFromPublicData.filter(i => !i.pending).map(i => i.id);

  return dispatch(queryUsers(teamIds));
};

export const showListing = (listingId, isOwn = false, useIntegration = false) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(showListingRequest(listingId));

  const params = {
    id: listingId,
    ...listingDetailIncludeParams(),
  };

  const show = isOwn
    ? useIntegration
      ? listingsAPI.show({
          ...params,
          id: listingId.uuid,
        })
      : sdk.ownListings.show(params)
    : sdk.listings.show(params);

  return show
    .then(response => {
      const data = isOwn && useIntegration ? response.data : response;
      const listing = denormalisedResponseEntities(data)?.[0];

      dispatch(addMarketplaceEntities(data));
      const cachedPayouts = listing?.author?.attributes?.profile?.publicData?.payoutsEnabled;
      dispatch(fetchAuthorPayoutsEnabled(listing?.author?.id?.uuid, cachedPayouts));
      fetchListingTeamIds(listing, dispatch);

      return data;
    })
    .catch(e => {
      console.error('Error fetching listing:', e);
    });
};

export const queryExperiences = () => (dispatch, getState, sdk) => {
  dispatch(queryExperiencesRequest());

  return sdk.listings
    .query({
      pub_type: config.listingTypes['live-room'],
      per_page: QUERY_EXPERIENCES_RESULT_PAGE_SIZE,
      ...listingDetailIncludeParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(queryExperiencesSuccess(response));
      return response;
    })
    .catch(e => dispatch(queryLiveRoomsError(storableError(e))));
};

export const sendEnquiry = (listingId, message) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());
  const bodyParams = {
    transition: TRANSITION_ENQUIRE,
    processAlias: config.bookingProcessAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendEnquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

export const queryUsers = queryIds => async (dispatch, getState, sdk) => {
  dispatch(queryUsersRequest());

  return usersAPI
    .byIds(queryIds || [])
    .then(response => {
      dispatch(addMarketplaceEntities(response.data));
      dispatch(queryUsersSuccess(response.data, queryIds));

      return response.data;
    })
    .catch(e => dispatch(queryUsersError(storableError(e))));
};

export const requestFetchAvailabilityExceptions = fetchParams => (dispatch, getState, sdk) => {
  dispatch(fetchAvailabilityExceptionsRequest(fetchParams));

  return availabilityExceptionsAPI
    .query(fetchParams, { expand: true })
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response.data);
      dispatch(fetchAvailabilityExceptionsSuccess(availabilityExceptions));
      return availabilityExceptions;
    })
    .catch(e => {
      return dispatch(fetchAvailabilityExceptionsError(storableError(e)));
    });
};

export const createFreeSubscription = params => (dispatch, getState, sdk) => {
  dispatch(createFreeSubscriptionRequest());

  const { listingId, userId } = params;

  return subscriptionsAPI
    .createFreeSubscription({ listingId, userId })
    .then(response => {
      dispatch(createFreeSubscriptionSuccess(response.data));
      dispatch(addMarketplaceEntities(response.data));
      dispatch(fetchCurrentUser());

      return response.data;
    })
    .catch(e => {
      return dispatch(createFreeSubscriptionError(storableError(e)));
    });
};

/**
 * Reserve a spot on an episode in one tap, from the Live Room schedule.
 *
 * An episode has nothing to pay for, so the member no longer goes through the
 * /booking confirmation page (approved by Yuko, 17 Oct 2026). The booking is
 * still made, in place: the member's own calendar lists episodes from their
 * bookings, and the booking records the RSVP and adds them to the episode's
 * conversation (BookingPage.duck). The message is the one the free-join flow
 * sends with it, if any.
 */
export const reserveEpisodeSpot = ({
  listing,
  timeslot,
  timeslotId,
  message,
}) => async dispatch => {
  const id = timeslot?.id || timeslotId;
  const bookingDates = timeslot?.bookingDates;
  if (!listing?.id || !id) return null;

  // An episode missing what a booking needs says so on its row rather than
  // doing nothing.
  if (!bookingDates) {
    dispatch({ type: RESERVE_EPISODE_ERROR, payload: id });
    return null;
  }

  dispatch({ type: RESERVE_EPISODE_REQUEST, payload: timeslot.id });

  const bookingParams = {
    listingId: listing.id,
    bookingStart: bookingDates.bookingStart,
    bookingEnd: bookingDates.bookingEnd,
    metadata: {
      timeslot: {
        id: timeslot.id,
        name: timeslot.name,
        meeting: timeslot.meeting,
        quantity: timeslot.quantity || 1,
      },
      notifications: {
        customerNotification: false,
        providerNotification: true,
      },
      localBookingStartDates: {
        customer: formatDateString(bookingDates.bookingStart),
        ...(bookingDates.timeZone && {
          provider: formatDateString(bookingDates.bookingStart, bookingDates.timeZone),
        }),
      },
      organizationId: listing.attributes?.publicData?.organizationId,
    },
  };

  // requestBooking reports its own failure to the store and resolves without a
  // transaction, so a missing one is the error case here.
  const tx = await dispatch(requestBooking(bookingParams));
  if (!tx?.id) {
    dispatch({ type: RESERVE_EPISODE_ERROR, payload: timeslot.id });
    return null;
  }
  if (message) {
    await dispatch(sendMessage({ id: tx.id, message }));
  }
  dispatch({ type: RESERVE_EPISODE_SUCCESS, payload: timeslot.id });
  return tx;
};

/**
 * Fetch user's booked timeslots for a listing
 * This is used to prevent duplicate bookings
 */
export const fetchUserBookedTimeslots = listingId => async (dispatch, getState, sdk) => {
  try {
    // A signed-out visitor has no bookings; asking would only earn a 403.
    // authInfo reads the stored token, it is not an API call.
    const authInfo = await sdk.authInfo();
    if (!authInfo || authInfo.isAnonymous !== false) {
      dispatch(setBookedTimeslotIds([]));
      return [];
    }

    // Query transactions where current user is the customer for this listing
    const response = await sdk.transactions.query({
      only: 'order',
      listingId,
      include: ['listing'],
    });

    const transactions = response?.data?.data || [];

    // Extract timeslot IDs from transaction metadata
    const bookingIds = transactions
      .map(tx => tx?.attributes?.metadata?.timeslot?.id)
      .filter(Boolean);

    // Episodes reserved with Reserve my spot in Conversations are booked too,
    // so the schedule and the episode card always agree.
    let reservedIds = [];
    try {
      const summary = await episodeRsvpAPI.summary(listingId?.uuid || listingId);
      const mine = summary?.data?.data?.mine || {};
      reservedIds = Object.keys(mine).filter(id => mine[id]);
    } catch (e) {
      reservedIds = [];
    }
    const bookedTimeslotIds = [...new Set([...bookingIds, ...reservedIds])];

    dispatch(setBookedTimeslotIds(bookedTimeslotIds));
    return bookedTimeslotIds;
  } catch (error) {
    console.error('Error fetching user booked timeslots:', error);
    dispatch(setBookedTimeslotIds([]));
    return [];
  }
};

// loadData helper functions
const getCurrentListing = async listingId => {
  const currentListingResponse = await listingsAPI.show({ id: listingId });
  return currentListingResponse.data.data.data;
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);

  const tz = typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

  const today = new Date();
  const start = resetToStartOfDay(today, tz, 0);
  // Query range: today + 364 days
  const exceptionRange = 364;
  const end = resetToStartOfDay(today, tz, exceptionRange);

  // NOTE: in this template, we don't expect more than 100 exceptions.
  // If there are more exceptions, pagination kicks in and we can't use frontend sorting.
  const fetchExceptionsParams = {
    listingId: params.id,
    start,
    end,
  };

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];

  if (ownListingVariants.includes(params.variant)) {
    const { currentUser } = getState().user;

    const currentListing = await getCurrentListing(listingId.uuid);
    const organizationProfiles = await dispatch(fetchOrganizationProfiles());
    const isCurrentUserInvitedToOrganization = isUserInvitedToOrganization(
      currentListing,
      organizationProfiles
    );
    const isCurrentUserInvitedToTeam = isUserInvitedToTeam(currentUser, currentListing);
    const useIntegration = isCurrentUserInvitedToOrganization || isCurrentUserInvitedToTeam;

    const listingPromise = dispatch(showListing(listingId, true, useIntegration));
    const availabilityPromise = dispatch(requestFetchAvailabilityExceptions(fetchExceptionsParams));

    return Promise.all([listingPromise, availabilityPromise]);
  }

  // The server has no signed-in user and no localStorage: it loads the public part only.
  const isServer = typeof window === 'undefined';
  return dispatch(showListing(listingId)).then(() => {
    Promise.all([
      dispatch(fetchComments(listingId)),
      dispatch(queryExperiences()),
      dispatch(requestFetchAvailabilityExceptions(fetchExceptionsParams)),
      // The signed-in user is loaded at startup and refreshed by the actions that change it.
      isServer ? null : dispatch(loadClientData(params)),
    ]);
  });
};

// The visitor's own part of the page, loaded in the browser (see onlyClientDataNeeded in
// src/Routes.js): their organizations, their booked episodes and their recently viewed rooms.
export const loadClientData = params => dispatch => {
  const listingId = new UUID(params.id);
  return Promise.all([
    dispatch(fetchOrganizationProfiles()),
    dispatch(fetchUserBookedTimeslots(listingId)),
    dispatch(saveRecentlyViewedListingId(listingId.uuid)).then(() =>
      dispatch(queryRecentlyViewedListings())
    ),
  ]);
};

// Helper to get the correct SDK based on ownership
const getListingSdk = (isOwnListing, sdk) => {
  return isOwnListing ? sdk.ownListings : listingsAPI;
};

// Thunk for uploading video to Google Storage
export const uploadBehindTheScenesVideo = actionPayload => (dispatch, getState, sdk) => {
  const id = actionPayload.id;
  console.log('uploadBehindTheScenesVideo called with tempId:', id);
  dispatch(uploadVideoRequest(actionPayload));

  const formData = new FormData();
  formData.append('video', actionPayload.file);

  return googleStorageAPI.videos
    .upload(formData)
    .then(response => {
      // Debug: Log the response structure
      console.log('Video upload response:', response);

      // Handle both response.data and direct response
      const video = response.data || response;

      // Debug: Log what we're dispatching
      const { id: apiId, ...videoWithoutId } = video;
      const successPayload = {
        id, // Keep the original temp ID
        videoId: apiId, // Use API response ID as videoId
        ...videoWithoutId,
        file: actionPayload.file,
      };
      console.log('Dispatching uploadVideoSuccess with:', successPayload);

      return dispatch(uploadVideoSuccess(successPayload));
    })
    .catch(e => {
      console.error('Video upload error:', e);
      return dispatch(uploadVideoError({ id, error: storableError(e) }));
    });
};

// Thunk for updating listing with new behind the scenes
export const updateListingBehindTheScenes = (listingId, behindTheScenes, isOwnListing) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updateListingBehindTheScenesRequest());

  const updates = {
    id: listingId,
    publicData: { behindTheScenes },
  };

  const api = getListingSdk(isOwnListing, sdk);

  return api
    .update(updates)
    .then(response => {
      dispatch(updateListingBehindTheScenesSuccess(response));
      // Refresh the listing to show new behind the scenes
      dispatch(showListing(listingId, false, !isOwnListing));
      return response;
    })
    .catch(e => {
      dispatch(updateListingBehindTheScenesError(storableError(e)));
      throw e;
    });
};
